import { CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  PlusIcon,
  CheckCircleIcon as SolidCheckCircleIcon,
} from '@heroicons/react/24/solid';
import { Tooltip } from '@kira/checkpoint-shared-frontend';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import CreateCompetencyModal from 'components/CreateCompetencyModal/CreateCompetencyModal';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

import SidebarNavigationButton from './SidebarNavigationButton';

interface SidebarAssessmentProps {
  description: string;
  activePath: string;
  isCollapsed?: boolean;
  isShowing?: boolean;
  children?: React.ReactNode;
}

const SidebarAssessment = ({
  description,
  activePath,
  isCollapsed,
  isShowing = true,
  children,
}: SidebarAssessmentProps) => {
  const { isPractice } = useContext(AssessmentDetailsContext);
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsActive(false);
    const toLocation = pathname.split('/');
    if (toLocation.includes(activePath)) setIsActive(true);
  }, [pathname]);

  const isActiveClasses = twMerge(
    'bg-kira-blue-800',
    'bg-opacity-10',
    '!border-kira-blue-800'
  );

  const wrapperClasses = twMerge(
    'border-l-4',
    'border-transparent',
    'hover:bg-kira-blue-800',
    'hover:bg-opacity-10',
    'flex-grow',
    'flex',
    'items-center',
    'max-w-[19rem]',
    'px-4',
    'rounded-r-lg',
    'w-full',
    'p-3',
    isActive && isActiveClasses
  );

  return isShowing ? (
    <div className="flex flex-col items-start text-kira-slate-700">
      <button
        onClick={() => navigate('../steps/all-steps')}
        type="button"
        className="w-full"
      >
        <Tooltip
          textContent={description}
          isEnabled={isCollapsed}
          tooltipClassNames="left-16 !-mt-12 z-10"
        >
          <div className={wrapperClasses}>
            <div className="h-6 w-6">
              {isActive ? <SolidCheckCircleIcon /> : <CheckCircleIcon />}
            </div>
            {!isCollapsed ? (
              <div className="prose-button-lg list-inside truncate pl-2">
                {description}
              </div>
            ) : null}
          </div>
        </Tooltip>
      </button>
      {!isCollapsed ? (
        <div className="ml-6 w-[18.5rem]">
          {children}
          <SidebarNavigationButton
            description={
              isPractice ? t('Add question group') : t('Add competency')
            }
            Icon={<PlusIcon />}
            isCollapsed={isCollapsed}
            onClick={() => setIsOpen(true)}
            isActive={isOpen}
          />
        </div>
      ) : null}
      <CreateCompetencyModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </div>
  ) : null;
};

export default SidebarAssessment;
